// src/context/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase/config";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null); // merged user + profile data
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // ensure Firestore user doc exists and return merged data
  const ensureUserProfile = async (user, name = null, phone = null) => {
    if (!user?.uid) return;
    const userRef = doc(db, "users", user.uid);
    const snap = await getDoc(userRef);

    const baseName = name || user.displayName || (user.email ? user.email.split("@")[0] : "User");

    const userData = {
      uid: user.uid,
      name: baseName,
      email: user.email || null,
      phoneNumber: phone || null,
      photoURL: user.photoURL || null,
      updatedAt: serverTimestamp(),
    };

    if (!snap.exists()) {
      await setDoc(userRef, {
        ...userData,
        createdAt: serverTimestamp(),
      });
      return { ...userData };
    } else {
      // merge (do not overwrite createdAt)
      await setDoc(userRef, userData, { merge: true });
      return { ...snap.data(), ...userData };
    }
  };

  // Signup (email + password) and store phone + name in Firestore
  const signup = async (email, password, name = null, phone = null) => {
    // create auth user
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // update display name in Firebase Auth (optional)
    if (name) {
      try {
        await updateProfile(user, { displayName: name });
      } catch (e) {
        // non-fatal
        console.warn("updateProfile failed:", e);
      }
    }

    // Wait until onAuthStateChanged sees the authenticated user so Firestore security rules
    // that depend on request.auth.uid will allow writes. This avoids the timing issue.
    await new Promise((resolve) => {
      const unsub = onAuthStateChanged(auth, (usr) => {
        if (usr) {
          unsub();
          resolve(usr);
        }
      });
    });

    // Ensure Firestore doc exists and contains phone/name
    await ensureUserProfile(user, name, phone);

    // update local state & navigate
    const merged = await getDoc(doc(db, "users", user.uid));
    setCurrentUser({ ...(user || {}), ...(merged.exists() ? merged.data() : {}) });

    // go to chat (or home)
    navigate("/chat");
  };

  // Login
  const login = async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const user = cred.user;
    // Ensure profile exists (in case)
    await ensureUserProfile(user);
    navigate("/chat");
  };

  // Logout
  const logout = async () => {
    await signOut(auth);
    setCurrentUser(null);
    navigate("/login");
  };

  // Track auth state and load Firestore profile
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userRef = doc(db, "users", user.uid);
          const snap = await getDoc(userRef);
          if (!snap.exists()) {
            const data = await ensureUserProfile(user);
            setCurrentUser({ ...user, ...(data || {}) });
          } else {
            setCurrentUser({ ...user, ...snap.data() });
          }
        } catch (e) {
          console.error("Failed loading user profile:", e);
          setCurrentUser({ ...user });
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    currentUser,
    signup,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{!loading ? children : <div>Loading...</div>}</AuthContext.Provider>;
};
